import { useState, useEffect } from 'react'
import { authService } from '../services/auth'
import type { AuthUser, LoginCredentials, RegisterData } from '../services/auth'
import type { Session } from '@supabase/supabase-js'

export const useAuth = () => {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [session, setSession] = useState<Session | null>(null)

  useEffect(() => {
    let mounted = true
    let initTimedOut = false // Flag para rastrear se o timeout ocorreu

    // Função para inicializar a autenticação
    const initAuth = async () => {
      console.log('[useAuth] Iniciando autenticação...')
      try {
        // Verificar sessão atual
        console.log('[useAuth] Verificando sessão atual...')
        const currentSession = await authService.getCurrentSession()
        console.log('[useAuth] Sessão obtida:', currentSession ? 'Sessão ativa' : 'Sem sessão')

        // Se já timed out, não faz nada para não sobrescrever estado de login subsequente
        if (initTimedOut) {
          console.log('[useAuth] Init completou após timeout, ignorando resultado')
          return
        }

        if (currentSession?.user && mounted) {
          // Buscar dados completos do usuário
          console.log('[useAuth] Buscando perfil do usuário...')
          const currentUser = await authService.getCurrentUser()

          if (initTimedOut) {
            console.log('[useAuth] Perfil obtido após timeout, ignorando')
            return
          }

          console.log('[useAuth] Perfil obtido:', currentUser ? 'Sucesso' : 'Falhou')
          setUser(currentUser)
          setSession(currentSession)
        } else if (mounted) {
          console.log('[useAuth] Nenhuma sessão ativa')
          setUser(null)
          setSession(null)
        }
      } catch (error) {
        console.error('[useAuth] Erro ao inicializar autenticação:', error)
        if (mounted && !initTimedOut) {
          setUser(null)
          setSession(null)
        }
      } finally {
        if (mounted && !initTimedOut) {
          console.log('[useAuth] Finalizando inicialização, setando loading=false')
          setLoading(false)
        }
      }
    }

    // Wrapper com timeout para evitar loading infinito
    const initWithTimeout = async () => {
      const timeoutPromise = new Promise<void>((_, reject) => {
        setTimeout(() => {
          initTimedOut = true // Marca que ocorreu timeout
          reject(new Error('Timeout ao inicializar autenticação'))
        }, 15000) // Increased from 10000 to prevent timeout issues
      })

      try {
        await Promise.race([initAuth(), timeoutPromise])
      } catch (error) {
        console.error('[useAuth] Timeout ou erro na inicialização:', error)
        if (mounted) {
          // Timeout ocorreu, libera o loading para mostrar tela de login
          // Mas NÃO limpa o user/session se eles já tiverem sido setados (ex: por login manual)
          // Na verdade, no init inicial, user deve ser null mesmo se timeout
          if (initTimedOut) {
            // Se foi timeout, garantimos que loading false e user null (para mostrar login)
            setUser(null)
            setSession(null)
            setLoading(false)
          }
        }
      }
    }

    // Inicializar
    initWithTimeout()

    // Escutar mudanças de autenticação (Login/Logout)
    const { data: { subscription } } = authService.onAuthStateChange(async (authUser) => {
      console.log('[useAuth] Mudança de estado de auth detectada', authUser ? 'Usuário logado' : 'Usuário deslogado')

      // Se ocorrer uma mudança de estado (login/logout), não queremos que o initAuth tardio interfira
      // Então podemos considerar que o estado "inicial" já passou
      initTimedOut = true

      if (mounted) {
        // Se temos um usuário vindo do evento, confiamos nele
        setUser(authUser)

        if (authUser) {
          const currentSession = await authService.getCurrentSession()
          setSession(currentSession)
        } else {
          setSession(null)
        }

        setLoading(false)
      }
    })

    return () => {
      mounted = false
      subscription?.unsubscribe()
    }
  }, [])

  // Função de login
  const login = async (credentials: LoginCredentials) => {
    setLoading(true)
    try {
      const result = await authService.login(credentials)

      if (result.user) {
        setUser(result.user)
        const currentSession = await authService.getCurrentSession()
        setSession(currentSession)
      }

      return result
    } catch (error) {
      console.error('Erro no login:', error)
      return { user: null, error: 'Erro interno do servidor' }
    } finally {
      setLoading(false)
    }
  }

  // Função de logout
  const logout = async () => {
    setLoading(true)
    try {
      const result = await authService.logout()

      if (!result.error) {
        setUser(null)
        setSession(null)
      }

      return result
    } catch (error) {
      console.error('Erro no logout:', error)
      return { error: 'Erro interno do servidor' }
    } finally {
      setLoading(false)
    }
  }

  // Função de registro
  const register = async (data: RegisterData) => {
    setLoading(true)
    try {
      const result = await authService.register(data)

      if (result.user) {
        setUser(result.user)
        const currentSession = await authService.getCurrentSession()
        setSession(currentSession)
      }

      return result
    } catch (error) {
      console.error('Erro no registro:', error)
      return { user: null, error: 'Erro interno do servidor' }
    } finally {
      setLoading(false)
    }
  }

  // Função para resetar senha
  const resetPassword = async (email: string) => {
    try {
      return await authService.resetPassword(email)
    } catch (error) {
      console.error('Erro ao resetar senha:', error)
      return { error: 'Erro interno do servidor' }
    }
  }

  // Função para atualizar senha
  const updatePassword = async (newPassword: string) => {
    try {
      return await authService.updatePassword(newPassword)
    } catch (error) {
      console.error('Erro ao atualizar senha:', error)
      return { error: 'Erro interno do servidor' }
    }
  }

  // Função para atualizar perfil
  const updateProfile = async (updates: Partial<NonNullable<AuthUser['profile']>>) => {
    setLoading(true)
    try {
      const result = await authService.updateProfile(updates)

      if (result.user) {
        setUser(result.user)
      }

      return result
    } catch (error) {
      console.error('Erro ao atualizar perfil:', error)
      return { user: null, error: 'Erro interno do servidor' }
    } finally {
      setLoading(false)
    }
  }

  // Verificações de role
  const isAdmin = () => user?.profile?.role === 'admin'
  const isProfessor = () => user?.profile?.role === 'professor'
  const isAluno = () => user?.profile?.role === 'aluno'

  // Verificar se usuário está autenticado
  const isAuthenticated = () => !!user || !!session

  // Verificar se usuário tem permissão
  const hasRole = (roles: string[]) => {
    if (!user?.profile?.role) return false
    return roles.includes(user.profile.role)
  }

  return {
    // Estado
    user,
    session,
    loading,

    // Funções de autenticação
    login,
    logout,
    signOut: logout, // Alias para logout
    register,
    resetPassword,
    updatePassword,
    updateProfile,

    // Verificações
    isAuthenticated,
    isAdmin,
    isProfessor,
    isAluno,
    hasRole,

    // Dados do usuário
    profile: user?.profile,
    email: user?.email,
    userId: user?.id
  }
}

// Hook para verificar se usuário tem permissão específica
export const usePermission = (permission: string) => {
  const { user } = useAuth()

  return user?.profile?.role ?
    authService.checkPermission(user.profile.role, permission) :
    false
}

// Hook para verificar múltiplas permissões
export const usePermissions = (permissions: string[]) => {
  const { user } = useAuth()

  return permissions.reduce((acc, permission) => {
    acc[permission] = user?.profile?.role ?
      authService.checkPermission(user.profile.role, permission) :
      false
    return acc
  }, {} as Record<string, boolean>)
}